import React, { useState, useEffect } from "react";
import { Box, Button, FormControl, MenuItem, Select, Typography, Menu } from "@mui/material";

export default function UnitSelection({ units, onChangeUnits, onSelectUnit, onCopyConfig }) {
  const unitSelectionList = [
    { id: 0, name: "Base Suppression Unit (BSU)", value: "BSU" },
    { id: 1, name: "Continuous Flow (CF)", value: "CF" },
    { id: 2, name: "Early Detection (ED)", value: "ED" },
    { id: 3, name: "OnWatch (OW)", value: "OW" }
  ];

  const [unitDropdowns, setUnitDropdowns] = useState(units);
  const [anchorEl, setAnchorEl] = useState(null);
  const [copyTargetInfo, setCopyTargetInfo] = useState(null);

  useEffect(() => {
    onChangeUnits(unitDropdowns);
  }, [unitDropdowns, onChangeUnits]);

  const handleAddDropdown = () => {
    setUnitDropdowns((prev) => {
      const newId = prev.length > 0 ? prev[prev.length - 1].id + 1 : 1;
      return [...prev, { id: newId, value: "", active: false }];
    });
  };

  const handleRemoveDropdown = (id) => {
    setUnitDropdowns((prev) => prev.filter((dropdown) => dropdown.id !== id));
  };

  const handleDropdownChange = (id, newValue) => {
    setUnitDropdowns((prev) =>
      prev.map((dropdown) =>
        dropdown.id === id ? { ...dropdown, value: newValue } : dropdown
      )
    );
  };

  const handleSetActive = (id) => {
    setUnitDropdowns((prev) =>
      prev.map((dropdown) =>
        dropdown.id === id
          ? { ...dropdown, active: true }
          : { ...dropdown, active: false }
      )
    );
    if (onSelectUnit) {
      onSelectUnit(id);
    }
  };

  const handleOpenCopyMenu = (event, dropdown) => {
    const candidates = unitDropdowns.filter(
      (d) => d.id !== dropdown.id && d.value && d.value === dropdown.value
    );
    if (candidates.length > 0) {
      setCopyTargetInfo({ newUnitId: dropdown.id, candidates });
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseCopyMenu = () => {
    setAnchorEl(null);
    setCopyTargetInfo(null);
  };

  const handleSelectCopyCandidate = (sourceId) => {
    if (onCopyConfig && copyTargetInfo) {
      onCopyConfig(copyTargetInfo.newUnitId, sourceId);
    }
    handleCloseCopyMenu();
  };

  return (
    <Box>
      <Typography variant="h5" style={{ paddingBottom: "4%" }}>
        Select Unit(s)
      </Typography>

      {unitDropdowns.map((dropdown) => {
        const candidates = unitDropdowns.filter(
          (d) => d.id !== dropdown.id && d.value && d.value === dropdown.value
        );
        return (
          <Box
            key={dropdown.id}
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              minWidth: "60%",
              mb: 2,
              p: 1,
              border: dropdown.active ? "1px solid red" : "1px solid grey",
              borderRadius: 1,
              cursor: "pointer"
            }}
            onClick={() => handleSetActive(dropdown.id)}
          >
            <FormControl sx={{ width: "40%" }}>
              <Select
                labelId={`unit-selection-${dropdown.id}`}
                id={`unit-selection-${dropdown.id}`}
                value={dropdown.value}
                onChange={(event) =>
                  handleDropdownChange(dropdown.id, event.target.value)
                }
                sx={{ padding: "0px", maxHeight: "45px" }}
              >
                {unitSelectionList.map((pm) => (
                  <MenuItem key={pm.id} value={pm.value}>
                    {pm.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            {dropdown.value && candidates.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenCopyMenu(e, dropdown);
                }}
                sx={{ ml: 2 }}
              >
                Apply Existing Config
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveDropdown(dropdown.id);
              }}
              sx={{ ml: 2 }}
            >
              Remove
            </Button>
          </Box>
        );
      })}
      <Button
        style={{ marginBottom: "3%" }}
        variant="contained"
        onClick={handleAddDropdown}
      >
        Add Unit
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseCopyMenu}
      >
        {copyTargetInfo &&
          copyTargetInfo.candidates.map((candidate) => (
            <MenuItem
              key={candidate.id}
              onClick={() => handleSelectCopyCandidate(candidate.id)}
            >
              {unitSelectionList.find((unit) => unit.value === candidate.value)?.name} – Unit {candidate.id}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}
